import React, { useState } from 'react';
import { TableProperties, Network, GitFork, Database } from 'lucide-react';
import { MermaidDiagram } from '../../../../shared/components/MermaidDiagram.js';
import { erdMermaidChart, architectureMermaidChart } from './diagramsData.js';

export const DatabaseSchemaTab: React.FC = () => {
  const [diagramMode, setDiagramMode] = useState<'erd' | 'architecture'>('erd');

  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      {/* Diagram Control Bar */}
      <div className="bg-slate-900 text-slate-100 p-4 rounded-2xl border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <TableProperties className="w-4 h-4 text-amber-400" />
            <span>Diagram Visual Skema & Relasi Entitas Drizzle ORM</span>
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Diagram interaktif otomatis berbasis Mermaid.js yang memetakan tabel SQLite dan entitas Drizzle ORM.
          </p>
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-slate-950 rounded-xl border border-slate-800 shrink-0">
          <button
            onClick={() => setDiagramMode('erd')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer ${
              diagramMode === 'erd'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Network className="w-3.5 h-3.5" />
            <span>Diagram ERD</span>
          </button>

          <button
            onClick={() => setDiagramMode('architecture')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer ${
              diagramMode === 'architecture'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <GitFork className="w-3.5 h-3.5" />
            <span>Alur Arsitektur</span>
          </button>
        </div>
      </div>

      {/* Render Mermaid Diagram */}
      <MermaidDiagram
        chart={diagramMode === 'erd' ? erdMermaidChart : architectureMermaidChart}
      />

      {/* Drizzle ORM Model Schema Summary */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-2xs space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Database className="w-4 h-4 text-indigo-600" />
              <span>Ringkasan Definisi Model Drizzle ORM (`backend/core/database/schema.ts`)</span>
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Struktur pangkalan data SQLite UIBN terintegrasi dengan pengindeksan FTS5 real-time.
            </p>
          </div>
          <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-200">
            5 Entitas Tabel
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Table Users */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-100/80 px-2 py-0.5 rounded">
                users
              </span>
              <span className="text-[10px] text-slate-500 font-medium">Pengguna & Peran RBAC</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Menyimpan kredensial terenkripsi bcrypt, profil penulis/penerbit, serta hak akses (admin, penerbit, penulis).
            </p>
            <div className="text-[11px] font-mono text-slate-500 bg-white p-2 rounded border border-slate-200/60 leading-snug">
              id (PK) • name • email (UK) • password • role • publisher_code
            </div>
          </div>

          {/* Table Books */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded">
                books
              </span>
              <span className="text-[10px] text-slate-500 font-medium">Naskah & Edisi UIBN</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Entitas utama katalog naskah buku, kode UIBN unik, status verifikasi, dan relasi silsilah edisi turunan.
            </p>
            <div className="text-[11px] font-mono text-slate-500 bg-white p-2 rounded border border-slate-200/60 leading-snug">
              id (PK) • uibn (UK) • title • author_id (FK) • publisher_id (FK) • parent_book_id (FK) • edition_type
            </div>
          </div>

          {/* Table Scans */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded">
                scans
              </span>
              <span className="text-[10px] text-slate-500 font-medium">Telemetri Pemindaian</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Mencatat histori aktivitas pemindaian barcode/QR UIBN beserta metode scan, waktu, dan User Agent.
            </p>
            <div className="text-[11px] font-mono text-slate-500 bg-white p-2 rounded border border-slate-200/60 leading-snug">
              id (PK) • book_id (FK) • uibn • scan_method • scanned_at
            </div>
          </div>

          {/* Table Notifications */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded">
                notifications
              </span>
              <span className="text-[10px] text-slate-500 font-medium">Notifikasi Sistem</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Notifikasi langsung terkait perubahan status verifikasi, peringatan scan, dan pemberitahuan sistem.
            </p>
            <div className="text-[11px] font-mono text-slate-500 bg-white p-2 rounded border border-slate-200/60 leading-snug">
              id (PK) • userId (FK) • bookId (FK) • title • type • is_read
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
